import { useCallback, useRef } from 'react';

import { EditorDataV2 } from '../types';

export type UseLineBasedHeightResult = {
  getHeightForLines: (lines: number) => number | undefined;
};

/**
 * Provides a utility to compute the overlay box height for a given number of
 * lines. The first time lines > 1 is requested, the current height is saved.
 * When lines drops back to 1, the saved height is restored.
 * Returns undefined when no height change is needed.
 */
const useLineBasedHeight = (draftEditorData: EditorDataV2) => {
  const naturalHeightRef = useRef<number | null>(null);

  const getHeightForLines = useCallback(
    (lines: number): number | undefined => {
      if (lines > 1) {
        if (naturalHeightRef.current === null) {
          naturalHeightRef.current = draftEditorData.getIn(['size', 'height']);
        }

        const fontSize = draftEditorData.getIn([
          'editor',
          'textStyle',
          'fontSize',
        ]);
        const lineHeight = draftEditorData.getIn([
          'editor',
          'textStyle',
          'lineHeight',
        ]);

        return fontSize * lineHeight * lines;
      }

      if (naturalHeightRef.current !== null) {
        const height = naturalHeightRef.current;

        naturalHeightRef.current = null;

        return height;
      }

      return undefined;
    },
    [draftEditorData],
  );

  return { getHeightForLines };
};

export default useLineBasedHeight;
